/**
 * GrantsMatrix — per-report batch level editor.
 * Subjects (rows) × tenant_report_id (single target) × level (cell dropdown).
 */
import { tokens } from '@/lib/tokens.js';
import { useState } from 'react';
import { useGrantsMatrix } from '../../hooks/use-admin-grants.js';
import type { GrantMatrixChange } from '../../lib/admin-client.js';

const LEVELS = ['view', 'execute', 'full', 'none'] as const;
type LevelOrNone = (typeof LEVELS)[number];

const s = {
  container: {
    backgroundColor: tokens.colors.bgCardDark,
    borderRadius: tokens.radius.card,
    boxShadow: '0 4px 6px -1px rgba(0,0,0,.1)',
    overflow: 'hidden',
  },
  header: {
    padding: '1rem 1.5rem',
    borderBottom: `1px solid ${tokens.colors.borderDark}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: { fontSize: '1rem', fontWeight: 600, color: tokens.colors.textDark, margin: 0 },
  body: { padding: '1.5rem' },
  input: {
    backgroundColor: '#0f172a',
    border: `1px solid ${tokens.colors.borderDark}`,
    borderRadius: tokens.radius.input,
    color: tokens.colors.textDark,
    padding: '0.5rem 0.75rem',
    fontSize: '0.875rem',
    outline: 'none',
    fontFamily: 'inherit',
    width: '100%',
    boxSizing: 'border-box' as const,
    marginBottom: '0.5rem',
  },
  select: {
    backgroundColor: '#0f172a',
    border: `1px solid ${tokens.colors.borderDark}`,
    borderRadius: tokens.radius.input,
    color: tokens.colors.textDark,
    padding: '0.375rem 0.625rem',
    fontSize: '0.875rem',
    outline: 'none',
  },
  btn: {
    backgroundColor: tokens.colors.primary,
    color: '#fff',
    border: 'none',
    borderRadius: tokens.radius.button,
    padding: '0.5rem 1rem',
    fontSize: '0.875rem',
    cursor: 'pointer',
    fontWeight: 500,
  },
  table: { width: '100%', borderCollapse: 'collapse' as const, marginBottom: '1rem' },
  th: {
    padding: '0.625rem 0.75rem',
    textAlign: 'left' as const,
    fontSize: '0.75rem',
    fontWeight: 600,
    color: tokens.colors.textMutedDark,
    textTransform: 'uppercase' as const,
    borderBottom: `1px solid ${tokens.colors.borderDark}`,
    backgroundColor: tokens.colors.bgCardDark,
  },
  td: {
    padding: '0.625rem 0.75rem',
    fontSize: '0.875rem',
    color: tokens.colors.textDark,
    borderBottom: '1px solid rgba(55,65,81,0.4)',
  },
};

interface MatrixRow {
  _key: string;
  subject_type: 'role' | 'user';
  subject_id: string;
  level: LevelOrNone;
}

interface GrantsMatrixProps {
  tenantId: string;
  onClose?: () => void;
}

export function GrantsMatrix({ tenantId, onClose }: GrantsMatrixProps) {
  const [tenantReportId, setTenantReportId] = useState('');
  const [rows, setRows] = useState<MatrixRow[]>([
    { _key: crypto.randomUUID(), subject_type: 'role', subject_id: '', level: 'none' },
  ]);

  const matrix = useGrantsMatrix(tenantId);

  function addRow() {
    setRows((prev) => [
      ...prev,
      { _key: crypto.randomUUID(), subject_type: 'role', subject_id: '', level: 'none' },
    ]);
  }

  function updateRow(idx: number, key: Exclude<keyof MatrixRow, '_key'>, value: string) {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [key]: value } : r)));
  }

  function removeRow(idx: number) {
    setRows((prev) => prev.filter((_, i) => i !== idx));
  }

  function handleSave() {
    const changes: GrantMatrixChange[] = rows
      .filter((r) => r.subject_id.trim().length > 0)
      .map(({ _key: _, ...r }) => ({
        subject_type: r.subject_type,
        subject_id: r.subject_id.trim(),
        level: r.level === 'none' ? null : r.level,
      }));

    matrix.mutate({ tenant_report_id: tenantReportId, changes }, { onSuccess: () => onClose?.() });
  }

  return (
    <div style={s.container}>
      <div style={s.header}>
        <h2 style={s.title}>Grants Matrix</h2>
        <button
          type="button"
          style={{
            ...s.btn,
            backgroundColor: 'transparent',
            border: `1px solid ${tokens.colors.borderDark}`,
            color: tokens.colors.textDark,
          }}
          onClick={onClose}
          aria-label="Close matrix"
        >
          Close
        </button>
      </div>
      <div style={s.body}>
        <div style={{ marginBottom: '1rem' }}>
          <label
            htmlFor="matrix-tenant-report-id"
            style={{
              fontSize: '0.875rem',
              fontWeight: 500,
              color: tokens.colors.textDark,
              display: 'block',
              marginBottom: '0.375rem',
            }}
          >
            Tenant Report ID
          </label>
          <input
            id="matrix-tenant-report-id"
            style={s.input}
            value={tenantReportId}
            onChange={(e) => setTenantReportId(e.target.value)}
            placeholder="trp_…"
            aria-label="Tenant report ID for matrix"
          />
        </div>

        <table style={s.table} aria-label="Grants matrix editor">
          <thead>
            <tr>
              <th style={s.th}>Subject type</th>
              <th style={s.th}>Subject ID</th>
              <th style={s.th}>Level</th>
              <th style={s.th} />
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={row._key}>
                <td style={s.td}>
                  <select
                    style={s.select}
                    value={row.subject_type}
                    onChange={(e) => updateRow(idx, 'subject_type', e.target.value)}
                    aria-label="Subject type"
                  >
                    <option value="role">Role</option>
                    <option value="user">User</option>
                  </select>
                </td>
                <td style={s.td}>
                  <input
                    style={{ ...s.input, marginBottom: 0 }}
                    value={row.subject_id}
                    onChange={(e) => updateRow(idx, 'subject_id', e.target.value)}
                    placeholder="ID…"
                    aria-label="Subject ID"
                  />
                </td>
                <td style={s.td}>
                  <select
                    style={s.select}
                    value={row.level}
                    onChange={(e) => updateRow(idx, 'level', e.target.value)}
                    aria-label="Grant level"
                  >
                    {LEVELS.map((l) => (
                      <option key={l} value={l}>
                        {l}
                      </option>
                    ))}
                  </select>
                </td>
                <td style={s.td}>
                  <button
                    type="button"
                    style={{
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: tokens.colors.textMutedDark,
                      cursor: 'pointer',
                      fontSize: '1rem',
                      padding: '0.25rem',
                    }}
                    onClick={() => removeRow(idx)}
                    aria-label="Remove row"
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <button
            type="button"
            style={{
              ...s.btn,
              backgroundColor: 'transparent',
              border: `1px solid ${tokens.colors.borderDark}`,
              color: tokens.colors.textDark,
            }}
            onClick={addRow}
            aria-label="Add row"
          >
            + Add row
          </button>
          <button
            type="button"
            style={{ ...s.btn, opacity: matrix.isPending || !tenantReportId ? 0.6 : 1 }}
            onClick={handleSave}
            disabled={matrix.isPending || !tenantReportId}
            aria-label="Save grants matrix"
          >
            {matrix.isPending ? 'Saving…' : 'Save matrix'}
          </button>
        </div>

        {matrix.isSuccess && (
          <output
            style={{
              display: 'block',
              color: '#86efac',
              fontSize: '0.875rem',
              marginTop: '0.75rem',
            }}
          >
            Applied {(matrix.data as { applied: number }).applied} change(s).
          </output>
        )}
        {matrix.isError && (
          <p
            style={{ color: tokens.colors.error, fontSize: '0.875rem', marginTop: '0.75rem' }}
            role="alert"
          >
            Error saving matrix.
          </p>
        )}
      </div>
    </div>
  );
}
